import React, {
    useState, useEffect, useContext
} from 'react';
import {
    DialogTitle,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogContent,
    Dialog,
} from 'shared/components/ui/dialog';
import {
    Input
} from 'shared/components/ui/input';
import {
    MagnifyingGlassIcon,
} from '@radix-ui/react-icons';
import {
    AppLoader
} from 'shared/components/LoaderSpinner';
import {
    Button
} from 'shared/components/ui/button';
import {
    Checkbox
} from 'shared/components/ui/checkbox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'shared/components/ui/table';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    organizationService
} from 'shared/api';

const UpdateSubOrganizationDialog = ({
    show,
    setShow,
    subOrg,
    setSubOrg,
    addNewNotification,
    onSubOrgUpdated,
}) => {
    const [btnLoading, setBtnLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const {
        selectedOrganization, refreshSubOrganizations
    } = useContext(OrganizationContext) as any;

    const getSubOrgId = () => subOrg._id ?? subOrg.id;

    const getData = async () => {
        if (!show) return;
        try {
            const orgUsers = await organizationService.getOrganizationUsers(selectedOrganization._id);
            setMembers(orgUsers.data);
            const subOrgMemberIds = (subOrg.members || []).map(String);
            setSelectedMembers(
                orgUsers.data.filter((member) => subOrgMemberIds.includes(String(member._id)))
            );
        } catch { /* empty */

        } finally {
            setLoadingMembers(false);
        }
    };

    useEffect(() => {
        if (show) {
            setLoadingMembers(true);
            setSearchQuery('');
            getData();
        }
    }, [show, selectedOrganization, subOrg]);

    const handleUpdateSubOrganization = () => {
        setBtnLoading(true);
        const memberIds = selectedMembers.map((member) => member._id);
        const subOrgId = getSubOrgId();
        organizationService.updateSubOrganizations(selectedOrganization._id, subOrgId, {
            members: memberIds,
        })
            .then(async (res) => {
                const updatedMembers = res.data?.members ?? memberIds;
                setSubOrg({
                    ...subOrg,
                    members: updatedMembers,
                });
                await refreshSubOrganizations();
                onSubOrgUpdated?.(subOrgId, updatedMembers);
                setShow(false);
                addNewNotification('Sub-Organization updated successfully', 'success');
            })
            .catch((error) => {

                if (error?.response?.status === 404) {
                    addNewNotification('Sub-Organization not found', 'danger');
                } else if (error?.response?.status === 401) {
                    addNewNotification(
                        'You are not authorized to update this sub-organization',
                        'danger'
                    );
                } else {
                    addNewNotification('Something went wrong', 'danger');
                }
            })
            .finally(() => {
                setBtnLoading(false);
            });
    };

    const isMemberSelected = (memberId) =>
        selectedMembers.some((m) => m._id === memberId);

    const toggleMember = (member) => {
        if (isMemberSelected(member._id)) {
            setSelectedMembers(selectedMembers.filter((m) => m._id !== member._id));
        } else {
            setSelectedMembers([...selectedMembers, member]);
        }
    };

    const filteredMembers = members.filter(
        (member) =>
            member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            member.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const allFilteredSelected = filteredMembers.length > 0
        && filteredMembers.every((member) => isMemberSelected(member._id));

    const handleSelectAllToggle = () => {
        if (allFilteredSelected) {
            const filteredIds = new Set(filteredMembers.map((m) => m._id));
            setSelectedMembers(selectedMembers.filter((m) => !filteredIds.has(m._id)));
        } else {
            const existingIds = new Set(selectedMembers.map((m) => m._id));
            const toAdd = filteredMembers.filter((m) => !existingIds.has(m._id));
            setSelectedMembers([...selectedMembers, ...toAdd]);
        }
    };

    return (
        <Dialog open={show} onOpenChange={setShow}>
            <DialogContent className="max-h-[85vh] w-full max-w-2xl p-0 gap-0 overflow-hidden bg-white rounded-lg shadow-lg">
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
                    <DialogTitle className="text-xl font-semibold">
                        Update Sub Organization
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        Select members for <span className="font-medium text-foreground">{subOrg.name}</span>
                    </DialogDescription>
                </DialogHeader>

                <div className="px-6 py-4 space-y-3">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            id="member-search"
                            placeholder="Search members..."
                            type="search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 h-9 text-sm"
                        />
                    </div>

                    <p className="text-xs text-muted-foreground">
                        {selectedMembers.length} member{selectedMembers.length !== 1 ? 's' : ''} selected
                    </p>

                    <div className="border rounded-md overflow-hidden">
                        <div className="overflow-auto max-h-[360px]">
                            <Table>
                                <TableHeader className="sticky top-0 z-10 bg-slate-50">
                                    <TableRow className="hover:bg-transparent border-b border-slate-200">
                                        <TableHead className="w-12 px-4">
                                            <Checkbox
                                                checked={allFilteredSelected}
                                                onCheckedChange={handleSelectAllToggle}
                                                disabled={loadingMembers || filteredMembers.length === 0}
                                                aria-label="Select all members"
                                            />
                                        </TableHead>
                                        <TableHead className="px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                            Name
                                        </TableHead>
                                        <TableHead className="px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                            Email
                                        </TableHead>
                                        <TableHead className="px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                            Role
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loadingMembers ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="h-40 text-center">
                                                <AppLoader size={48} />
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredMembers.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="h-24 text-center text-sm text-muted-foreground">
                                                {searchQuery ? 'No members match your search.' : 'No members found.'}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredMembers.map((member) => (
                                            <TableRow
                                                key={member._id}
                                                className="cursor-pointer hover:bg-slate-50/80 border-b border-slate-100 last:border-b-0"
                                                onClick={() => toggleMember(member)}
                                            >
                                                <TableCell className="w-12 px-4" onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        checked={isMemberSelected(member._id)}
                                                        onCheckedChange={() => toggleMember(member)}
                                                        aria-label={`Select ${member.name}`}
                                                    />
                                                </TableCell>
                                                <TableCell className="px-4 py-3 text-sm font-medium text-slate-900">
                                                    {member.name}
                                                </TableCell>
                                                <TableCell className="px-4 py-3 text-sm text-slate-600">
                                                    {member.email}
                                                </TableCell>
                                                <TableCell className="px-4 py-3 text-sm capitalize text-slate-600">
                                                    {member.role}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>

                <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 sm:justify-end gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setShow(false)}
                        disabled={btnLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdateSubOrganization}
                        disabled={btnLoading}
                    >
                        {btnLoading ? 'Updating...' : 'Update Sub Organization'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UpdateSubOrganizationDialog;
